import { useState } from "react";
import { Copy, RefreshCcw, Check } from "lucide-react";

import { copyTextToClipboard } from "@/lib/clipboard";
import {
    buildWhatsAppLink,
    buildMailtoLink,
    logSendEvent,
    normalizeWhatsAppNumber,
} from "@/lib/sendApi";
import { toast } from "sonner";

/**
 * SendPanel — shown below the AnalysisCard rewrite tabs.
 *
 *   Section A : Direct send (WhatsApp / Gmail) — only renders if the chat
 *               has the matching contact detail. If neither exists, the
 *               whole section is hidden and a soft hint nudges the user
 *               toward chat settings.
 *   Section B : Copy message — always visible.
 *   Section C : Send original message instead — always visible, low emphasis.
 *
 * Sending is local-only: WhatsApp and Gmail open via deep links; the actual
 * send happens in the user's WhatsApp / mail client. The panel logs a
 * send_event to Mongo on every action so ToneMirror can later surface
 * "you accept your rewrite 84% of the time" insights.
 */
const SendPanel = ({
    chat,
    improvedText,
    originalText,
    style,
    intent,
    messageId,
    onAfterAccept,
    onAfterOriginal,
}) => {
    const [copied, setCopied] = useState(false);

    const phone = chat?.phone_number || null;
    const email = chat?.email_address || null;
    const waLink = buildWhatsAppLink(phone, improvedText);
    const mailLink = buildMailtoLink(email, improvedText);

    /**
     * Build the send-event payload once — used by both WhatsApp and Gmail
     * onClick handlers. We log on the same user gesture that triggers the
     * <a> navigation: the log fires synchronously BEFORE the browser
     * navigates, and the fire-and-forget axios call survives the tab open.
     */
    const buildLogPayload = (platform) => ({
        chat_id: chat?.id || null,
        message_id: messageId || null,
        send_platform: platform,
        rewrite_accepted: true,
        original_text: originalText || null,
        improved_text: improvedText || null,
        style: style || null,
        intent: intent || null,
    });

    const handleCopy = async () => {
        const ok = await copyTextToClipboard(improvedText);
        if (!ok) {
            toast.error("Couldn't copy — please select and copy manually.");
            return;
        }
        setCopied(true);
        toast.success("Copied to clipboard");
        logSendEvent({
            chat_id: chat?.id || null,
            message_id: messageId || null,
            send_platform: "copy",
            rewrite_accepted: true,
            original_text: originalText || null,
            improved_text: improvedText || null,
            style: style || null,
            intent: intent || null,
        });
        onAfterAccept?.({ platform: "copy" });
        window.setTimeout(() => setCopied(false), 1600);
    };

    const handleSendOriginal = async () => {
        // Copy the original to the clipboard for parity with the rewrite path.
        await copyTextToClipboard(originalText || "");
        toast.message("Original copied — send it your way.");
        logSendEvent({
            chat_id: chat?.id || null,
            message_id: messageId || null,
            send_platform: "original",
            rewrite_accepted: false,
            original_text: originalText || null,
            improved_text: improvedText || null,
            style: style || null,
            intent: intent || null,
        });
        onAfterOriginal?.();
    };

    const hasAnyContact = !!waLink || !!mailLink;

    return (
        <div
            data-testid="send-panel"
            className="flex flex-col gap-3 pt-1"
        >
            {/* Section A — Direct send.
                Anchor tags (NOT window.open) so the browser handles the
                navigation as a native user gesture. window.open from inside
                an iframe (e.g. Emergent preview frame) triggers Chrome's
                cross-origin opener block → api.whatsapp.com refused to
                connect (ERR_BLOCKED_BY_RESPONSE). Anchor tags route through
                the user-gesture path and work in every embed context. */}
            {hasAnyContact ? (
                <div
                    data-testid="send-direct"
                    className="flex flex-col sm:flex-row sm:flex-wrap gap-2"
                >
                    {waLink && (
                        <a
                            href={waLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="btn-send-whatsapp"
                            onClick={() => {
                                logSendEvent(buildLogPayload("whatsapp"));
                                onAfterAccept?.({ platform: "whatsapp" });
                            }}
                            className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full bg-[#25D366] hover:bg-[#1FB957] text-white text-sm font-semibold shadow-sm transition-colors no-underline"
                        >
                            <WhatsAppIcon />
                            Send on WhatsApp
                        </a>
                    )}
                    {mailLink && (
                        <a
                            href={mailLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="btn-send-gmail"
                            onClick={() => {
                                logSendEvent(buildLogPayload("gmail"));
                                onAfterAccept?.({ platform: "gmail" });
                            }}
                            className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full bg-[#D44638] hover:bg-[#B23121] text-white text-sm font-semibold shadow-sm transition-colors no-underline"
                        >
                            <GmailIcon />
                            Send via Gmail
                        </a>
                    )}
                </div>
            ) : (
                <p
                    data-testid="send-no-contact-hint"
                    className="text-[12.5px] text-muted-foreground"
                >
                    Add a number or email in chat settings to enable direct
                    sending&nbsp;↗
                </p>
            )}

            {/* Section B — Copy message (always visible) */}
            <button
                type="button"
                data-testid="btn-send-copy"
                onClick={handleCopy}
                className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full bg-[#5E7153] hover:bg-[#4A5941] text-white text-sm font-semibold shadow-sm transition-colors w-full sm:w-fit"
            >
                {copied ? (
                    <>
                        <Check className="h-4 w-4" />
                        Copied
                    </>
                ) : (
                    <>
                        <Copy className="h-4 w-4" />
                        Copy message
                    </>
                )}
            </button>

            {/* Section C — Send original (low emphasis, always visible) */}
            <button
                type="button"
                data-testid="btn-send-original-low"
                onClick={handleSendOriginal}
                className="self-start text-[12.5px] text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors inline-flex items-center gap-1.5"
            >
                <RefreshCcw className="h-3 w-3" />
                Send original message instead
            </button>
        </div>
    );
};

// --- Inline brand icons (no extra deps) ---
const WhatsAppIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .12 5.4.12 12.04c0 2.12.55 4.18 1.6 6L0 24l6.13-1.6a11.96 11.96 0 0 0 5.92 1.51h.01c6.55 0 11.94-5.4 11.94-12.04A11.97 11.97 0 0 0 20.52 3.48Zm-8.46 18.4h-.01a9.9 9.9 0 0 1-5.05-1.39l-.36-.21-3.64.95.97-3.54-.23-.36a9.97 9.97 0 0 1-1.54-5.3c0-5.5 4.49-9.97 10-9.97a9.94 9.94 0 0 1 9.95 9.97c0 5.5-4.49 9.85-10.09 9.85Zm5.45-7.43c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.47a9 9 0 0 1-1.66-2.06c-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.47 1.07 2.88 1.22 3.08.15.2 2.11 3.22 5.11 4.52.72.31 1.27.5 1.7.64.71.23 1.36.2 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
    </svg>
);

const GmailIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <path d="M1.5 5.25A2.25 2.25 0 0 1 3.75 3h16.5a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 20.25 21H3.75A2.25 2.25 0 0 1 1.5 18.75V5.25Zm2.25-.75a.75.75 0 0 0-.6 1.2L12 12.94l8.85-7.24a.75.75 0 0 0-.6-1.2H3.75Zm17.25 2.41-8.4 6.87a.75.75 0 0 1-.9 0L3.3 6.91v11.84c0 .42.34.75.75.75h16.2a.75.75 0 0 0 .75-.75V6.91Z" />
    </svg>
);

export default SendPanel;

// Helper kept here so callers can render a soft contact-status pill without
// re-deriving the rule. Returns null / "phone" / "email" / "both".
export function describeContactPresence(chat) {
    const phone = chat?.phone_number;
    const email = chat?.email_address;
    if (normalizeWhatsAppNumber(phone) && email) return "both";
    if (normalizeWhatsAppNumber(phone)) return "phone";
    if (email) return "email";
    return null;
}
